/*
 * Copyright 2026, Airspan Networks
 * All Rights Reserved.
 */

import { SxProps } from "@mui/material";
import { IFilterSelectBox } from "../../Shared/Filter/types";
import { useSelector } from "react-redux";
import { useStoreDispatch } from "../../../store/hooks";
import { useMemo } from "react";
import { useRegionDisplay } from "../../../pages/Administrations/AccessControls/Regions/hooks";
import { selectedGeoRegionIdsSelector } from "../../../store/api/admin/selectors";
import { IGeoRegion } from "../../../store/api/admin/types";
import { AppState } from "../../../store";
import {
  inventoryModelSelector,
  inventoryStateSelector,
  inventoryTechnologySelector,
  inventoryViewSelector,
  inventoryRegionFilterIdsSelector,
} from "../../../store/filters/Inventory/selectors";
import {
  setSelectedModel,
  setSelectedState,
  setSelectedTechnology,
  setRegionFilterIds,
} from "../../../store/filters/Inventory/reducer";
import { useGetInventoryRadioNodeModelsQuery, useGetInventoryStatusQuery } from "../../../store/api/devices/deviceApiSlice";
import {
  deviceStatusSelector,
  deviceMaintenanceModeSelector,
  deviceKpiSyncSelector,
  deviceConfigStatusSelector,
  deviceSiteGroupSelector,
  deviceRegionFilterIdsSelector,
} from "../../../store/filters/Nodes/selectors";
import {
  setSelectedDeviceStatus,
  setMaintenanceMode,
  setSelectedDeviceKpiStatus,
  setSelectedDeviceConfigStatus,
  setSelectedDeviceSiteGroup,
  setRegionFilterIds as setNavRegionFilterIds,
} from "../../../store/filters/Nodes/reducer";
import { DEVICESTATUS } from "../../../globals/globals";
import { constStr } from "../../../globals/constants";

export const useInventoryFilter = () => {
  const dispatch = useStoreDispatch();

  const selectedModel = useSelector(inventoryModelSelector);
  const selectedState = useSelector(inventoryStateSelector);
  const selectedTechnology = useSelector(inventoryTechnologySelector);
  const view = useSelector(inventoryViewSelector);
  const regionFilterIds = useSelector<AppState, string[]>(inventoryRegionFilterIdsSelector);
  const selectedGeoRegionIds = useSelector(selectedGeoRegionIdsSelector);

  const navDeviceStatus = useSelector(deviceStatusSelector);
  const navMaintenanceMode = useSelector(deviceMaintenanceModeSelector);
  const navPmCollected = useSelector(deviceKpiSyncSelector);
  const navConfigStatus = useSelector(deviceConfigStatusSelector);
  const navSiteGroup = useSelector(deviceSiteGroupSelector);
  const navRegionFilterIds = useSelector(deviceRegionFilterIdsSelector);

  const { geoRegions } = useRegionDisplay();

  const regionOptions = useMemo(() => {
    if (!geoRegions || geoRegions.length === 0) return [];

    const baseRegions = selectedGeoRegionIds && selectedGeoRegionIds.length
      ? geoRegions.filter((region: IGeoRegion) => selectedGeoRegionIds.includes(region.objectKey))
      : geoRegions;

    return baseRegions
      .map((region: IGeoRegion) => ({ value: region.objectKey, text: region.name }))
      .sort((a, b) => (a.text || "").localeCompare(b.text || ""));
  }, [geoRegions, selectedGeoRegionIds]);

  const selectedGeoRegionNames = useMemo(() => {
    if (!selectedGeoRegionIds || selectedGeoRegionIds.length === 0 || !geoRegions) return undefined;
    const names = geoRegions
      .filter((region: IGeoRegion) => selectedGeoRegionIds.includes(region.objectKey))
      .map((region: IGeoRegion) => region.name);
    return names.length > 0 ? names : undefined;
  }, [geoRegions, selectedGeoRegionIds]);

  const { data: modelList } = useGetInventoryRadioNodeModelsQuery(selectedGeoRegionNames);

  const statusQueryType = view === "radionodes" ? "RadioNode" : "Cell";
  const { data: stateList } = useGetInventoryStatusQuery(statusQueryType);

  const stateOptions = useMemo(() => {
    if (!stateList || stateList.length === 0) return [];
    return stateList.map((s: string) => ({ value: s, text: s })).sort((a, b) => a.text.localeCompare(b.text));
  }, [stateList]);

  const modelOptions = useMemo(() => {
    if (!modelList || modelList.length === 0) return [];
    return modelList.map((m: string) => ({ value: m, text: m })).sort((a, b) => a.text.localeCompare(b.text));
  }, [modelList]);

  const regionFilter: IFilterSelectBox[] = [
    {
      id: "regionFilter",
      label: "REGION",
      value: (regionFilterIds || []) as unknown as string,
      setValue: setRegionFilterIds,
      values: regionOptions,
      width: "200px",
      multiple: true,
    },
  ];

  const modelFilter: IFilterSelectBox[] = view === "radionodes"
    ? [
        {
          id: "model",
          label: "MODEL",
          value: selectedModel || "",
          setValue: setSelectedModel,
          values: modelOptions,
          width: "160px",
          preserveOptionCase: true,
        },
      ]
    : [];

  const technologyFilter: IFilterSelectBox[] = view === "cells"
    ? [
        {
          id: "technology",
          label: "TECHNOLOGY",
          value: selectedTechnology || "",
          setValue: setSelectedTechnology,
          values: [
            { value: "NRCell", text: "NR Cell" },
            { value: "LTECell", text: "LTE Cell" },
          ],
          width: "150px",
          preserveOptionCase: true,
        },
      ]
    : [];

  const stateFilter: IFilterSelectBox[] = [
    {
      id: "state",
      label: "STATE",
      value: selectedState || "",
      setValue: setSelectedState,
      values: stateOptions,
      width: "180px",
      preserveOptionCase: true,
    },
  ];

  const navStatusFilter: IFilterSelectBox[] = navDeviceStatus
    ? [
        {
          id: "navStatus",
          label: "STATUS",
          value: navDeviceStatus,
          setValue: setSelectedDeviceStatus,
          values: [{ value: DEVICESTATUS.TIMEOUT, text: "Disconnected" }],
        },
      ]
    : [];

  const navMaintenanceModeFilter: IFilterSelectBox[] = navMaintenanceMode
    ? [
        {
          id: "navMaintenanceMode",
          label: "MAINTENANCE",
          value: navMaintenanceMode,
          setValue: setMaintenanceMode,
          values: [{ value: "true", text: "Active" }],
        },
      ]
    : [];

  const navKpiSyncFilter: IFilterSelectBox[] = navPmCollected
    ? [
        {
          id: "navKpiSync",
          label: "KPI SYNC",
          value: navPmCollected,
          setValue: setSelectedDeviceKpiStatus,
          values: [{ value: "false", text: "Off" }],
        },
      ]
    : [];

  const navConfigStatusFilter: IFilterSelectBox[] = navConfigStatus
    ? [
        {
          id: "navConfigStatus",
          label: "CONFIG STATUS",
          value: navConfigStatus,
          setValue: setSelectedDeviceConfigStatus,
          values: [],
        },
      ]
    : [];

  const navSiteGroupFilter: IFilterSelectBox[] =
    navSiteGroup === constStr.ungrouped
      ? [
          {
            id: "navSiteGroup",
            label: "SITE GROUP",
            value: navSiteGroup,
            setValue: setSelectedDeviceSiteGroup,
            values: [{ value: constStr.ungrouped, text: "Ungrouped" }],
          },
        ]
      : [];

  // Chip for geo-region selected from the left-side "Device Groups" panel.
  // Only shown when the top-dropdown REGION filter is not already active,
  // to avoid duplicate REGION chips.
  const navRegionFilter: IFilterSelectBox[] =
    navRegionFilterIds && navRegionFilterIds.length > 0 && (!regionFilterIds || regionFilterIds.length === 0)
      ? [
          {
            id: "navRegionFilter",
            label: "REGION",
            value: navRegionFilterIds as unknown as string,
            setValue: setNavRegionFilterIds,
            values: regionOptions,
            width: "200px",
            multiple: true,
          },
        ]
      : [];

  const filters: IFilterSelectBox[] = [
    ...regionFilter,
    ...navRegionFilter,
    ...modelFilter,
    ...technologyFilter,
    ...stateFilter,
    ...navStatusFilter,
    ...navMaintenanceModeFilter,
    ...navKpiSyncFilter,
    ...navConfigStatusFilter,
    ...navSiteGroupFilter,
  ];

  const formControlSx: SxProps = { minWidth: 100, marginRight: "10px" };
  const searchTooltip = view === "cells"
    ? "Search by 'Cell', 'Cell Id', 'Description', 'Hostname', 'Serial Number' fields for quick access."
    : "Search by 'Radio Node', 'Radio Node Index', 'Description', 'Hostname', 'Serial Number' fields for quick access.";

  return {
    dispatch,
    filters,
    formControlSx,
    searchTooltip,
  };
};
